import * as Sentry from '@sentry/react'
import { hasInviteId, redactRecordingEvent } from './sentry-redact'

/*
  Session Replay records `location.href` in the meta event of every full snapshot — when it starts
  and at each checkout — and `beforeAddRecordingEvent` does not reach that event. So Replay never
  runs while the URL holds an invite ID: it is not started on a page opened from an accept-invite
  link, and it is stopped if a recording session reaches such a URL. It comes back once the URL is
  clean (see `syncReplayWithUrl`).
*/

let deferred = false
let stoppedOnInviteUrl = false

const createReplayIntegration = () =>
  Sentry.replayIntegration({
    // Mask all text and block all media so secrets rendered in the DOM (newly issued API keys,
    // accept-invite links) are never captured in the recording.
    maskAllText: true,
    blockAllMedia: true,
    beforeAddRecordingEvent: redactRecordingEvent,
  })

// The Replay integration to pass to `Sentry.init`, or none when the page was opened on an invite URL.
export const getInitialReplayIntegrations = (href: string = window.location.href) => {
  if (hasInviteId(href)) {
    deferred = true
    return []
  }
  return [createReplayIntegration()]
}

// Call on every in-app navigation. Stops Replay on a URL holding an invite ID; on a clean URL,
// starts the Replay deferred at load, or resumes buffering the one stopped here.
export const syncReplayWithUrl = (href: string): void => {
  if (!Sentry.getClient()) return
  if (hasInviteId(href)) {
    const replay = Sentry.getReplay()
    if (replay?.getReplayId() !== undefined) {
      stoppedOnInviteUrl = true
      void replay?.stop()
    }
    return
  }
  if (deferred) {
    deferred = false
    Sentry.addIntegration(createReplayIntegration())
  } else if (stoppedOnInviteUrl) {
    stoppedOnInviteUrl = false
    Sentry.getReplay()?.startBuffering()
  }
}
